import { useRef } from 'react';
import cn from 'classnames';
// Styles
import styles from './StreamContentTabs.module.scss';

export enum StreamContentTabKey {
  Highlights = 'highlights',
  Matchups = 'matchups',
  Media = 'media',
  Participants = 'participants',
  UpcomingEvents = 'upcoming-events',
}

type TabDefinition = {
  key: StreamContentTabKey;
  label: string;
  icon: string;
};

const TAB_DEFINITIONS: TabDefinition[] = [
  {
    key: StreamContentTabKey.Highlights,
    label: 'Highlights',
    icon: '/images/stream-highlights/highlights.svg',
  },
  {
    key: StreamContentTabKey.Matchups,
    label: 'Matchups',
    icon: '/images/stream-tabs/swords.svg',
  },
  {
    key: StreamContentTabKey.Media,
    label: 'Media',
    icon: '/images/stream-tabs/play-square.svg',
  },
  {
    key: StreamContentTabKey.Participants,
    label: 'Participants',
    icon: '/images/stream-tabs/users.svg',
  },
  {
    key: StreamContentTabKey.UpcomingEvents,
    label: 'Upcoming Events',
    icon: '/images/stream-tabs/calendar.svg',
  },
];

export const getTabPanelId = (key: StreamContentTabKey): string =>
  `stream-tab-panel-${key}`;

type StreamContentTabsProps = {
  availableTabs: StreamContentTabKey[];
  activeTab: StreamContentTabKey | null;
  onTabChange: (key: StreamContentTabKey) => void;
  hideWhenSingleTab?: boolean;
};

const StreamContentTabs = ({
  availableTabs,
  activeTab,
  onTabChange,
  hideWhenSingleTab = false,
}: StreamContentTabsProps): JSX.Element | null => {
  const tabRefs = useRef<
    Partial<Record<StreamContentTabKey, HTMLButtonElement | null>>
  >({});

  const tabs = TAB_DEFINITIONS.filter(({ key }) => availableTabs.includes(key));

  if (tabs.length === 0 || (hideWhenSingleTab && tabs.length === 1)) {
    return null;
  }

  const focusTab = (key: StreamContentTabKey) => {
    tabRefs.current[key]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const lastIndex = tabs.length - 1;
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextKey = tabs[nextIndex].key;
    onTabChange(nextKey);
    focusTab(nextKey);
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner} role="tablist" aria-label="Event content">
        {tabs.map(({ key, label, icon }, index) => {
          const isActive = key === activeTab;

          return (
            <button
              key={key}
              type="button"
              role="tab"
              id={`stream-tab-${key}`}
              ref={(node) => {
                tabRefs.current[key] = node;
              }}
              aria-selected={isActive}
              aria-controls={getTabPanelId(key)}
              tabIndex={isActive ? 0 : -1}
              className={cn(styles.tab, { [styles.tabActive]: isActive })}
              onClick={() => onTabChange(key)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <img
                src={icon}
                alt=""
                aria-hidden="true"
                className={styles.icon}
              />
              <span className={styles.label}>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default StreamContentTabs;
